import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { SEOHead } from '@/components/SEOHead';
import { Mail } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  const { language } = useLanguage();

  const content = language === 'fr' ? {
    title: "Politique de confidentialité",
    lastUpdated: "Dernière mise à jour : 24 juin 2025",
    sections: [
      {
        title: "1. Responsable du traitement",
        content: "Gaius, dont le siège social est situé à Paris, France. SIREN : 803876457. Email de contact : contact@gaiustech.com"
      },
      {
        title: "2. Types de données collectées",
        content: `Nous collectons les types de données suivantes :
        • Données d'identification : nom, prénom, adresse email
        • Données professionnelles : nom de l'entreprise/cabinet, fonction
        • Données de navigation : adresse IP, cookies, données de connexion
        • Données de formation : inscriptions, participation aux sessions
        • Données de communication : messages via formulaires de contact`
      },
      {
        title: "3. Finalités du traitement",
        content: `Vos données sont traitées pour :
        • Gérer les inscriptions aux formations
        • Fournir nos services de formation en IA juridique
        • Répondre à vos demandes de contact
        • Améliorer nos services et notre site web
        • Respecter nos obligations légales
        • Vous envoyer des informations sur nos formations (avec votre consentement)`
      },
      {
        title: "4. Base légale",
        content: `Le traitement de vos données repose sur :
        • L'exécution d'un contrat (formations)
        • Votre consentement (newsletter, cookies non essentiels)
        • Notre intérêt légitime (amélioration des services)
        • Le respect d'obligations légales`
      },
      {
        title: "5. Destinataires des données",
        content: `Vos données peuvent être partagées avec :
        • Nos prestataires techniques (hébergement, paiement)
        • Nos partenaires de formation (le cas échéant)
        • Les autorités compétentes (si requis par la loi)`
      },
      {
        title: "6. Durée de conservation",
        content: `Nous conservons vos données :
        • Données clients : 3 ans après la fin de la relation contractuelle
        • Données prospects : 3 ans après le dernier contact
        • Données de navigation : 13 mois maximum
        • Données comptables : 10 ans (obligation légale)`
      },
      {
        title: "7. Vos droits",
        content: `Conformément au RGPD, vous disposez des droits suivants :
        • Droit d'accès à vos données
        • Droit de rectification
        • Droit d'effacement ("droit à l'oubli")
        • Droit à la limitation du traitement
        • Droit à la portabilité
        • Droit d'opposition
        • Droit de retirer votre consentement
        
        Pour exercer ces droits, contactez-nous à : contact@gaiustech.com`
      },
      {
        title: "8. Cookies",
        content: `Notre site utilise des cookies pour :
        • Le fonctionnement technique du site (cookies essentiels)
        • L'analyse d'audience (avec votre consentement)
        • La personnalisation du contenu
        
        Vous pouvez gérer vos préférences cookies via les paramètres de votre navigateur.`
      },
      {
        title: "9. Sécurité",
        content: "Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre l'accès non autorisé, la perte ou la destruction."
      },
      {
        title: "10. Réclamations",
        content: "Vous avez le droit de déposer une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : www.cnil.fr"
      }
    ]
  } : {
    title: "Privacy Policy", 
    lastUpdated: "Last updated: June 24, 2025",
    sections: [
      {
        title: "1. Data Controller",
        content: "Gaius, headquarters located in Paris, France. SIREN: 803876457. Contact email: contact@gaiustech.com"
      },
      {
        title: "2. Types of Data Collected",
        content: `We collect the following types of data:
        • Identification data: first name, last name, email address
        • Professional data: company/firm name, position
        • Navigation data: IP address, cookies, connection data
        • Training data: registrations, session participation
        • Communication data: messages via contact forms`
      },
      {
        title: "3. Purposes of Processing",
        content: `Your data is processed to:
        • Manage training registrations
        • Provide our legal AI training services
        • Respond to your contact requests
        • Improve our services and website
        • Comply with legal obligations
        • Send you information about our training (with your consent)`
      },
      {
        title: "4. Legal Basis",
        content: `The processing of your data is based on:
        • Contract performance (training services)
        • Your consent (newsletter, non-essential cookies)
        • Our legitimate interest (service improvement)
        • Compliance with legal obligations`
      },
      {
        title: "5. Data Recipients",
        content: `Your data may be shared with:
        • Our technical service providers (hosting, payment)
        • Our training partners (where applicable)
        • Competent authorities (if required by law)`
      },
      {
        title: "6. Retention Period",
        content: `We retain your data:
        • Customer data: 3 years after end of contractual relationship
        • Prospect data: 3 years after last contact
        • Navigation data: 13 months maximum
        • Accounting data: 10 years (legal obligation)`
      },
      {
        title: "7. Your Rights",
        content: `In accordance with GDPR, you have the following rights:
        • Right of access to your data
        • Right to rectification
        • Right to erasure ("right to be forgotten")
        • Right to restriction of processing
        • Right to data portability
        • Right to object
        • Right to withdraw consent
        
        To exercise these rights, contact us at: contact@gaiustech.com`
      },
      {
        title: "8. Cookies",
        content: `Our site uses cookies for:
        • Technical website operation (essential cookies)
        • Audience analysis (with your consent)
        • Content personalization
        
        You can manage your cookie preferences via your browser settings.`
      },
      {
        title: "9. Security",
        content: "We implement appropriate technical and organizational measures to protect your data against unauthorized access, loss, or destruction."
      },
      {
        title: "10. Complaints",
        content: "You have the right to file a complaint with the relevant data protection authority in your jurisdiction."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={language === 'fr' ? "Politique de confidentialité" : "Privacy Policy"}
        description={language === 'fr' 
          ? "Politique de confidentialité de Gaius - Formation IA pour avocats. Découvrez comment nous protégeons vos données personnelles."
          : "Gaius Privacy Policy - AI training for lawyers. Learn how we protect your personal data."}
        canonicalUrl="https://gaius-tech.com/privacy-policy"
        alternateUrlFr="https://gaius-tech.com/confidentialite"
        alternateUrlEn="https://gaius-tech.com/privacy-policy"
      />
      <div className="bg-gradient-to-r from-primary to-purple-600 text-primary-foreground py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              {content.title}
            </h1>
            <p className="text-blue-100">{content.lastUpdated}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="space-y-8">
            {content.sections.map((section, index) => (
              <div key={index}>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {section.title}
                </h2>
                <div className="text-gray-700 whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center space-x-2 text-gray-600">
              <Mail className="w-5 h-5" />
              <span>Contact : contact@gaiustech.com</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
